import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import { TFunction } from 'i18next';

import HomeApprovalDetails from '@/modules/home/components/PageContent/Approvals/ApprovalDetails';
import { IHome } from '@/modules/home/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';

import Tick from '~/images/shared/CheckMarkWithoutBG.svg';
import Close from '~/images/shared/CloseWithoutBG.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';
import Minus from '~/images/shared/Minus.svg';

export const getApprovalStatus = (
  status: IHome.ApprovalStatus,
  isActive: 0 | 1
) => {
  const styles = {
    approved: {
      circleBg: isActive === 1 ? 'bg-secondary-90' : 'bg-neutralVariant-90',
      barBg: isActive === 1 ? 'bg-secondary-20' : 'bg-neutralVariant-90',
      color: isActive === 1 ? 'text-green-300' : 'text-surface-10',
      icon: <Tick width='30' height='30' />,
    },
    rejected: {
      circleBg: isActive === 1 ? 'bg-error-90' : 'bg-neutralVariant-90',
      barBg: isActive === 1 ? 'bg-error-400' : 'bg-neutralVariant-90',
      color: isActive === 1 ? 'text-error-20' : 'text-surface-10',
      icon: <Close width='23' height='23' />,
    },
    pending: {
      circleBg:
        isActive === 1 ? 'bg-neutralVariant-96' : 'bg-neutralVariant-90',
      barBg: isActive === 1 ? 'bg-states-waiting' : 'bg-neutralVariant-90',
      color: isActive === 1 ? 'text-tertiary-20' : 'text-surface-10',
      icon: <Minus width='14' height='2' />,
    },
  };

  const ApprovalStatusMap = {
    1: {
      status: 'pending',
      message: 'pending',
      ...styles.pending,
    },
    2: {
      status: 'approved',
      message: 'approved',
      ...styles.approved,
    },
    3: {
      status: 'rejected',
      message: 'rejected',
      ...styles.rejected,
    },
    4: {
      status: 'approved',
      message: 'edited_and_approved',
      ...styles.approved,
    },
    5: {
      status: 'pending',
      message: 'rejected_and_pending',
      ...styles.pending,
    },
    6: {
      status: 'edited',
      message: 'edited_and_pending',
      ...styles.pending,
    },
  };

  return ApprovalStatusMap[status];
};

const sortApprovalStatuses = (a: IHome.IApprover, b: IHome.IApprover) => {
  return a.hierarchy - b.hierarchy;
};

export const getApprovalsTableColumn = (t: TFunction) => {
  const columnHelper = createColumnHelper<IHome.IApprovalSchema>();

  return [
    columnHelper.accessor('jobName', {
      header: t('Operation'),
      cell: ({ getValue, row: { original } }) => (
        <div className='flex items-center gap-8'>
          <CircularBgImage
            bgColor='bg-blue-100'
            imageClassName='scale-124'
            imageSrc={original.imageCrop}
          />
          <div>
            <Typography>{getValue()}</Typography>
            <Typography variant='caption' className='text-surface-10'>
              {original.supplierName}
            </Typography>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('jobInstanceDate', {
      header: t('date'),
      cell: ({ getValue }) => format(parseISO(getValue()), 'dd/MM/yyyy'),
    }),
    columnHelper.accessor('field', {
      header: t('location'),
      cell: ({ getValue, row: { original } }) => (
        <PartialListWithTooltip
          imgProps={{
            bgColor: 'bg-blue-100',
            imageClassName: 'scale-125',
          }}
          items={[
            {
              description: getValue(),
              imageURL: original.imageCrop,
            },
          ]}
        />
      ),
    }),
    columnHelper.accessor('dailyWage', {
      header: t('Agreement Type'),
      cell: ({ getValue }) =>
        getValue() === 1 ? t('daily') : t('Placework Rate'),
    }),
    columnHelper.accessor('approvers', {
      header: t('Status'),
      cell: ({
        row: {
          original: { approvers, jobInstanceId },
        },
      }) => {
        const sorted = approvers.sort(sortApprovalStatuses);

        return (
          <div className='flex items-center gap-8'>
            <div className='flex gap-[2px]'>
              {sorted.map((item, idx) => (
                <div
                  className={classNames(
                    'h-3 w-16 rounded-[10px]',
                    getApprovalStatus(item.approvalStatus, item.activeApprover)
                      ?.barBg
                  )}
                  key={`${jobInstanceId}${item.approvalStatus}${idx}`}
                />
              ))}
            </div>
            <TippyTooltip
              padding='p-0'
              content={<HomeApprovalDetails data={sorted} id={jobInstanceId} />}
            >
              <span className='cursor-pointer text-surface-10'>
                <InfoCircle width='16' height='16' />
              </span>
            </TippyTooltip>
          </div>
        );
      },
    }),
  ];
};
